<template>
    <tailwind-layout title="Tenant Screening: Add Property">
        <template #header>
            <div class="mb-4 px-4 sm:px-6 lg:px-8 mx-auto flex justify-between items-center">
                <h1 class="font-semibold text-2xl leading-tight">Tenant Screening: Add Property</h1>
                <div class="flex items-center space-x-4">
                    <inertia-link class="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10" :href="route('properties.index')">
                        <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                </div>
            </div>
        </template>
        <div class="overflow-hidden shadow-xl rounded-lg p-6 bg-white">
            <form @submit.prevent="store">
                <div class="space-y-12">
                    <div class="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div>
                            <h2 class="text-base/7 font-semibold text-gray-900">Rental Property Address</h2>
                            <p class="mt-1 text-sm/6 text-gray-600">
                                This is where the Rental Property information will be entered.
                            </p>
                            <p class="mt-3 text-sm/6 text-gray-600 font-bold">
                                Address Lookup
                            </p>
                            <p class="mt-1 text-sm/6 text-gray-600">
                                We use Google Maps to find the address. Please start typing the address and select the correct address from the dropdown. If you experience an issue with a bad zipcode or other address, please contact us at the chat window below.
                            </p>
                            <p class="mt-1 text-sm/6 text-gray-600">
                                Only the Address 2 input is available for manual entry. The rest of the address fields are automatically filled in when you select an address from the dropdown.
                            </p>
                        </div>

                        <div class="grid max-w-4xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                            <div class="sm:col-span-full">
                                <label for="address-lookup" class="block text-sm font-medium text-gray-700">
                                    Address Lookup
                                </label>
                                <div class="mt-1">
                                    <div class="mt-1 relative rounded-md">
                                        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                            <font-awesome-icon :icon="faMapMarkerAlt" class="text-gray-400" />
                                        </div>
                                        <GMapAutocomplete
                                            id="address_lookup"
                                            ref="autocompleteRef"
                                            placeholder="Start typing address..."
                                            @place_changed="initAutocomplete"
                                            :options="{
                                                componentRestrictions: { country: ['US', 'VI', 'AS', 'GU', 'PR', 'MP'] }
                                            }"
                                            v-bind:class="'px-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border border-gray-300 rounded-md pl-10'"
                                            data-demo-target="address_lookup"
                                        >
                                        </GMapAutocomplete>
                                    </div>
                                    <p class="text-xs text-gray-500">Please include postal code for accuracy.</p>
                                </div>
                            </div>

                            <div class="sm:col-span-3">
                                <label for="address-1" v-bind:class="(form.errors.address_1)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Address 1 <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="address_1" v-model="form.address_1" readonly :error="form.errors.address_1" type="text" v-bind:class="(form.errors.address_1)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md bg-gray-100':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-gray-100  bg-gray-100'">
                                    <p v-text="form.errors.address_1" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-3">
                                <label for="address-2" v-bind:class="(form.errors.address_2)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Address 2
                                </label>
                                <div class="mt-1">
                                    <input id="address-2" v-model="form.address_2" type="text" v-bind:class="(form.errors.address_2)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'">
                                </div>
                            </div>

                            <div class="sm:col-span-2 sm:col-start-1">
                                <label for="city" v-bind:class="(form.errors.city)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    City <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="city" v-model="form.city" readonly type="text" v-bind:class="(form.errors.city)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md  bg-gray-100'">
                                    <p v-text="form.errors.city" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="state" v-bind:class="(form.errors.state)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    State <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input v-model="form.state" type="hidden">
                                    <select id="state" disabled v-bind:value="form.state" v-model="form.state" :error="form.errors.state" v-bind:class="(form.errors.state)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-gray-100'">
                                        <option v-for="(state, index) in states" :value="index">{{ state }}</option>
                                    </select>
                                    <p v-text="form.errors.state" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="zip_code" v-bind:class="(form.errors.zip_code)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Zip Code <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="zip_code" v-model="form.zip_code" readonly type="text" v-bind:class="(form.errors.zip_code)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-gray-100'">
                                    <p v-text="form.errors.zip_code" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                        </div> <!-- grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2 -->
                    </div> <!-- grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3 -->

                    <div class="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div>
                            <h2 class="text-base/7 font-semibold text-gray-900">Rental Property Details</h2>
                            <p class="mt-1 text-sm/6 text-gray-600">
                                Provide the Monthly Rent and Availability Date.
                            </p>
                        </div>

                        <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                            <div class="sm:col-span-full">
                                <label for="owner_name" v-bind:class="(form.errors.owner_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Owner/Landlord Name
                                </label>
                                <div class="mt-1">
                                    <input id="owner_name" v-model="form.owner_name" type="text" v-bind:class="(form.errors.owner_name)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'" data-demo-target="owner_name">
                                    <p v-text="form.errors.owner_name" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-3">
                                <label for="rent" v-bind:class="(form.errors.rent)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Monthly Rent
                                </label>

                                <div class="mt-1 relative rounded-md">
                                    <div class="absolute left-0 pl-3 flex items-center pointer-events-none" v-bind:class="(form.errors.rent)?'top-3':'inset-y-0'">
                                        <font-awesome-icon :icon="faDollarSign" class="text-gray-400" />
                                    </div>
                                    <input id="rent" v-model="form.rent" type="number" step="0.01" v-bind:class="(form.errors.rent)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'" data-demo-target="rent">
                                    <p v-text="form.errors.rent" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-3">
                                <label for="availability_date" v-bind:class="(form.errors.availability_date)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Availability Date <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1 relative rounded-md">
                                    <div class="absolute left-0 pl-3 flex items-center pointer-events-none" v-bind:class="(form.errors.availability_date)?'top-3':'inset-y-0'">
                                        <font-awesome-icon :icon="faCalendar" class="text-gray-400" />
                                    </div>
                                    <input id="availability_date" v-model="form.availability_date" type="date" v-bind:class="(form.errors.availability_date)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'" data-demo-target="availability_date">
                                    <p v-text="form.errors.availability_date" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                        </div> <!-- grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2 -->
                    </div> <!-- grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3 -->
                    
                    <div class="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div>
                            <h2 class="text-base/7 font-semibold text-gray-900">Property Applications</h2>
                            <p class="mt-1 text-sm/6 text-gray-600">
                                Are you ready to begin accepting applications today? If not, you can select no and come back later to begin accepting applications for this property.
                            </p>
                            <p class="mt-3 text-sm/6 text-gray-600">
                                <span class="font-bold">YES</span> I'm ready to accept applications today.
                            </p>
                            <p class="mt-1 text-sm/6 text-gray-600">
                                <span class="font-bold">NO</span> I want to add my property to my account, but don't want to accept applications yet.
                            </p>
                        </div>

                        <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                            <div class="sm:col-span-3">
                                <label for="status" v-bind:class="(form.errors.status)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Accept Applications <span class="text-red-700">*</span>
                                </label>

                                <div class="mt-6 space-y-6">
                                    <div class="flex items-center gap-x-3">
                                        <input
                                            id="status-yes"
                                            v-model="form.status"
                                            :value="1"
                                            type="radio"
                                            class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
                                            data-demo-target="status-yes"
                                        />
                                        <label for="status-yes" class="block text-sm/6 font-medium text-gray-900">
                                            YES
                                        </label>
                                    </div>
                                    <div class="flex items-center gap-x-3">
                                        <input
                                            id="status-no"
                                            v-model="form.status"
                                            :value="0"
                                            type="radio"
                                            class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
                                        />
                                        <label for="status-no" class="block text-sm/6 font-medium text-gray-900">
                                            NO
                                        </label>
                                    </div>
                                    <p v-text="form.errors.status" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                        </div> <!-- grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2 -->
                    </div> <!-- grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3 -->

                    <div class="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div>
                            <h2 class="text-base/7 font-semibold text-gray-900">Application Charges</h2>
                            <p class="mt-1 text-sm/6 text-gray-600">
                                Who do you want to pay the application fee? The applicant or you?
                            </p>
                        </div>

                        <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2" data-demo-target="applicant-fee-yes">
                            <div class="sm:col-span-full">
                                <label for="status" v-bind:class="(form.errors.is_applicant_fee)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Select who will pay for the Application <span class="text-red-700">*</span>
                                </label>

                                <div class="mt-6 space-y-6">
                                    <div class="flex items-center gap-x-3">
                                        <input
                                            id="applicant-fee-yes"
                                            v-model="form.is_applicant_fee"
                                            :value="1"
                                            type="radio"
                                            class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
                                        />
                                        <label for="applicant-fee-yes" class="block text-sm/6 font-medium text-gray-900">
                                            The Applicant will pay the $59 for the Application Fee.
                                        </label>
                                    </div>
                                    <div class="flex items-center gap-x-3">
                                        <input
                                            id="applicant-fee-no"
                                            v-model="form.is_applicant_fee"
                                            :value="0"
                                            type="radio"
                                            class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
                                        />
                                        <label for="applicant-fee-no" class="block text-sm/6 font-medium text-gray-900">
                                            I (the agent/landlord) will pay the $59 for the Application Fee on behalf of the applicant.
                                        </label>
                                    </div>
                                    <p v-text="form.errors.is_applicant_fee" class="text-red-500 text-sm"></p>
                                    <p v-text="payment_method_success" class="text-green-500 text-sm"></p>
                                </div>
                            </div>
                        </div> <!-- grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2 -->
                    </div> <!-- grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3 -->

                    <div class="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div>
                            <h2 class="text-base/7 font-semibold text-gray-900">Rent Payments</h2>
                            <p class="mt-1 text-sm/6 text-gray-600">
                                Are you interested in collecting rent payments online? Select yes and we will setup your property to begin accepting payments directly in your RentRisk account!
                            </p>
                        </div>

                        <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                            <div class="sm:col-span-full">
                                <label for="status" v-bind:class="(form.errors.status)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Please select an option below <span class="text-red-700">*</span>
                                </label>

                                <div class="mt-6 space-y-6">
                                    <div class="flex items-center gap-x-3">
                                        <input
                                            id="rent-payments-yes"
                                            v-model="form.type"
                                            :value="2"
                                            type="radio"
                                            class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
                                            data-demo-target="rent-payments-yes"
                                        />
                                        <label for="rent-payments-yes" class="block text-sm/6 font-medium text-gray-900">
                                            Yes! Setup my property to accept rent payments online.
                                        </label>
                                    </div>
                                    <div class="flex items-center gap-x-3">
                                        <input
                                            id="rent-payments-no"
                                            v-model="form.type"
                                            :value="0"
                                            type="radio"
                                            class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
                                        />
                                        <label for="rent-payments-no" class="block text-sm/6 font-medium text-gray-900">
                                            No, I already have a way to collect rent payments outside of RentRisk.
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div> <!-- grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2 -->
                    </div> <!-- grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3 -->

                    <div class="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div>
                            <div class="flex items-center">
                                <h2 class="text-base/7 font-semibold text-gray-900">Renter's Insurance</h2>
                                <img :src="appUrl('/sure-insurance-logo.svg')" class="rounded-lg bg-white ring-1 ring-gray-900/10 p-2 w-16 ml-6">
                            </div>
                            <p class="mt-1 text-sm/6 text-gray-600">
                                Do you require renter's insurance? We work with Sure to provide Renter's insurance.
                            </p>
                            <p class="mt-1 text-sm/6 text-gray-600">
                                Requiring renters insurance could save you and your tenants thousands. That's why 72% of RentRisk landlords require it.
                            </p>
                            <p class="mt-1 text-sm/6 text-gray-600">
                                We'll automatically ask your tenants to upload their own insurance policy or use our preferred partner, Sure. If they use Sure, our direct integration makes it easy for you to see the policy is active, the coverage amount, and more, all within RentRisk
                            </p>
                        </div>

                        <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                            <div class="sm:col-span-full">
                                <label for="status" v-bind:class="(form.errors.status)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Please select an option below <span class="text-red-700">*</span>
                                </label>

                                <div class="mt-6 space-y-6">
                                    <div class="flex items-center gap-x-3">
                                        <input
                                            id="rent-payments-yes"
                                            v-model="form.is_renters_insurance"
                                            :value="true"
                                            type="radio"
                                            class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
                                        />
                                        <label for="rent-payments-yes" class="block text-sm/6 font-medium text-gray-900">
                                            Yes! Send information to my applicant so they can get Renter's Insurance quick and easy!
                                        </label>
                                    </div>
                                    <div class="flex items-center gap-x-3">
                                        <input
                                            id="rent-payments-no"
                                            v-model="form.is_renters_insurance"
                                            :value="false"
                                            type="radio"
                                            class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
                                        />
                                        <label for="rent-payments-no" class="block text-sm/6 font-medium text-gray-900">
                                            No, I don't require renter's insurance or I want to use my own insurance company.
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div> <!-- grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2 -->
                    </div> <!-- grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3 -->
                </div> <!-- space-y-12 -->
                <div class="pt-5">
                    <div class="flex justify-end">
                        <inertia-link :href="route('home')" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400">Cancel</inertia-link>
                        <form-button :form="form" type="button" @click="showCAComplianceModal" class="ml-3" data-demo-target="save-button">
                            Save
                        </form-button>
                    </div>
                </div>
            </form>
        </div>
    </tailwind-layout>
    
    <TransitionRoot as="template" :show="cAComplianceModal">
        <Dialog as="div" class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <!-- This element is to trick the browser into centering the modal contents. -->
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                    <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <h1 class="font-semibold text-2xl leading-tight">For California Landlords and Property Managers</h1>
                            <h2 class="mt-4">California landlords and property managers are in compliance with the California Investigative Consumer Reporting Agencies Act (ICRAA).</h2>
                            <div class="flex gap-2 mt-4">
                        <label for="ca_icraa_compliance" class="flex gap-2 cursor-pointer">
                                    <input 
                                        id="ca_icraa_compliance"
                                        type="checkbox" 
                                        v-model="form.ca_icraa_compliance"
                                        class="focus:ring-rent-risk-blue-400 h-4 w-4 text-rent-risk-blue-400 border-gray-300 rounded mt-1"
                                    />
                                    <span class="text-sm">
                                        I have read and agree to the 
                                        <a href="https://www.icraa.com" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800">
                                            California Consumer Credit Reporting Agencies Act Disclosure and Authorization.
                                        </a>
                                    </span>
                                </label>
                            </div>
                            <p v-text="form.errors.ca_icraa_compliance" class="text-red-500 text-sm"></p>
                        </div>
                        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                            <button type="button" class="mt-3 w-full inline-flex justify-center rounded-md border border-indigo-300 shadow-sm px-4 py-2 bg-rent-risk-blue-400 text-white font-medium hover:bg-rent-risk-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" @click="store">Save</button>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>

    <!-- Add Credit Card Modal -->
    <add-card-popup :show="showAddCardModal" :stripe_data="stripe_data" @card-added="handleCardAdded" @close="closeCardModal()"></add-card-popup>
    
    <!-- Demo Widget -->
    <DemoWidget
        :page-identifier="'properties.create'"
        :steps="demoSteps"
        :auto-start="true"
        :show-help-icon="true"
    />
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import FormButton from '@/Shared/FormButton.vue'
import SelectInput from '@/Shared/SelectInput.vue'
import SwitchGroup from "@/Shared/SwitchGroup.vue";
import RadioGroup from "@/Shared/RadioGroup.vue";
import BannedCitiesAndStates from "@/Shared/BannedCitiesAndStates.vue";
import axios from "axios";
import SmallCards from "@/Shared/SmallCards.vue";
import {Dialog, DialogOverlay, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from "@headlessui/vue";
import {usePage} from "@inertiajs/vue3";
import Label from "@/Jetstream/Label.vue";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
    faCalendar,
    faChevronLeft,
    faDollarSign,
    faMapMarkerAlt
} from '@fortawesome/pro-solid-svg-icons'
import ToolTip from "@/Shared/ToolTip.vue";
import ModalJetstream from "@/Jetstream/Modal.vue";
import AlertCard from "@/Shared/AlertCard.vue";
import { computed } from "vue";
import AddCardPopup from '@/Shared/AddCardPopup.vue';
import DemoWidget from '@/Components/DemoWidget.vue'
import { getDemoSteps } from '@/Config/demoSteps.js'

export default {
    props: {
        states: Object,
        screening_plans: Object,
        two_step_addresses: Array,
        user_payment_method: Object,
        stripe_data: Array,
    },
    components: {
        FontAwesomeIcon,
        ToolTip,
        Label,
        DialogOverlay,
        DialogTitle,
        DialogPanel,
        TransitionChild,
        Dialog,
        TransitionRoot,
        SmallCards,
        BannedCitiesAndStates,
        SwitchGroup,
        TailwindLayout,
        FormButton,
        SelectInput,
        RadioGroup,
        ModalJetstream,
        AlertCard,
        AddCardPopup,
        DemoWidget
    },
    remember: 'form',
    data() {
        return {
            faCalendar,
            faChevronLeft,
            faDollarSign,
            faMapMarkerAlt,
            form: this.$inertia.form({
                owner_name: null,
                address_1: null,
                address_2: null,
                city: null,
                state: null,
                county: null,
                zip_code: null,
                rent: null,
                status: 1,
                screening_plan: 0,
                availability_date: null,
                application_link: null,
                type: 2,
                ca_icraa_compliance: false,
                is_applicant_fee: usePage().props.auth.user.is_agent_fee_all === 1 ? 0 : 1,
                is_agent_fee: usePage().props.auth.user.is_agent_fee_all,
                is_fee_setting_default: usePage().props.auth.user.is_fee_setting_default,
                is_renters_insurance: true,
            }),
            cAComplianceModal: false,
            twoStepAddress: false,
            payment_method: this.user_payment_method,
            payment_method_success: null,
            showAddCardModal: false,
            faChevronLeft,
            demoSteps: getDemoSteps('properties.create')
        }
    },
    mounted() {
        this.form.reset();
    },
    watch: {
        'form.is_agent_fee'(newValue, oldValue) {
            this.form.is_applicant_fee = newValue === 1 ? 0 : 1;
        },
        'form.is_applicant_fee'(newValue, oldValue) {
            this.form.is_agent_fee = newValue === 1 ? 0 : 1;
            if(this.payment_method === null) {
                if(newValue === 0) {
                    this.showAddCardModal = true;
                }
            }
        },
    },
    methods: {
        myToggleFunction: function(event, form){
            form.status = form.status === false;
        },
        acceptRentToggleFunction: function(event, form){
            form.type = form.type === false;
        },
        showCAComplianceModal() {
            if (this.form.state === 'CA') {
                this.cAComplianceModal = true;
            } else {
                this.store();
            }

        },
        store() {
            if(this.cAComplianceModal && !this.form.ca_icraa_compliance) {
                this.form.errors.ca_icraa_compliance = 'The California compliance must be acknowledged when the property is located in California.';
                return false;
            }
            this.cAComplianceModal = false;
            this.form.post(this.route('properties.store'))
        },
        initAutocomplete(place){
            // RAC-9990: Google Places sometimes returns a place without
            // address_components (establishment / regions / area-level results).
            // Surface inline error via form.errors.address_1 so the existing
            // <p v-text="form.errors.address_1"> in the template shows it.
            if (!place?.address_components) {
                this.form.errors.address_1 = "We couldn't read a full address from that result. Please pick a more specific result from the dropdown — make sure it's a street address, not a business or area.";
                return;
            }
            this.form.errors.address_1 = null;

            let street = null;
            let route = null;
            this.form.city = null;

            place.address_components.forEach(component => {
                const componentType = component.types[0];
                switch (componentType) {
                    case "street_number": {
                        street = `${component.long_name}`;
                        break;
                    }
                    case "route": {
                        route = component.short_name;
                        break;
                    }
                    case "postal_code": {
                        this.form.zip_code = `${component.long_name}`;
                        break;
                    }
                    case "locality":
                        this.form.city = component.long_name;
                        break;
                    case "neighborhood":
                        if (this.form.city === null) {
                            this.form.city = component.long_name;
                        }
                        break;
                    case "administrative_area_level_3":
                        if (this.form.city === null) {
                            this.form.city = component.long_name;
                        }
                        break;
                    case "administrative_area_level_2":
                        this.form.county = component.long_name;
                        break;
                    case "administrative_area_level_1": {
                        this.form.state = `${component.short_name}`;
                        break;
                    }
                    case "country": {
                        if(['VI', 'AS', 'GU', 'PR', 'MP'].includes(component.short_name)) {
                            this.form.state = `${component.short_name}`;
                        }
                        break;
                    }
                }
            });
            if(street === null || route === null) {
                this.form.address_1 = null;
                this.form.errors.address_1 = 'Address 1 not found. Enter the full address in Google search.';
            } else {
                this.form.address_1 = `${street} ${route}`
                this.form.errors.address_1 = null;
                // eslint-disable-next-line vue/no-undef-properties
                this.address_lookup = null;
                this.isPropertyTwoStep();
            }
            this.$refs.autocompleteRef.$refs.input.value = null;
        },

        isPropertyTwoStep(){
            const { county, city, state } = this.form;
            axios.get(route('properties.is-two-step'), {
                params: {
                    county: county,
                    city: city,
                    state: state,
                }
            }).then(response => {
                this.twoStepAddress = response.data;
            });
        },

        calculatePricingSum() {
            // eslint-disable-next-line vue/no-undef-properties
            this.pricingSum = 'test'
        },

        handleCardAdded({ payment_method, success }) {
            if(success) {
                this.payment_method = payment_method.payment_method;
                this.showAddCardModal = false;
                this.payment_method_success = payment_method.message;
                setTimeout(() => {
                    this.payment_method_success = null;
                }, 3000);
            }
        },

        closeCardModal() {
            this.form.is_applicant_fee = 1;
            this.form.is_agent_fee = 0;
            this.showAddCardModal = false;
            // eslint-disable-next-line vue/no-undef-properties
            this.isButtonActive = true;
        }
    }
}
</script>
